import React, { useState, useEffect } from "react";
import {
    Button,
    Dialog,
    DialogContent,
    IconButton,
    TextField,
    Typography,
    InputLabel,
    MenuItem,
    Select,
    FormControl,
    Stack,
} from "@mui/material";
import { CloseOutlined } from "@mui/icons-material";
import styled from "styled-components";
import OpaqueLoading from "../opaqueLoading/opaqueLoading";

const StyledDialogContent = styled(DialogContent)`
  padding: 16px;
`;

const ModalHeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
`;

const OlympiadSkuSubjectMappingModal = ({ onClose, fetchData, propData }) => {
    const [loading, setLoading] = useState(false);

    const [productName, setProductName] = useState("");
    const [skuCode, setSkuCode] = useState("");
    const [unitPrice, setUnitPrice] = useState("");

    const [boardId, setBoardId] = useState("");
    const [boardName, setBoardName] = useState("");
    const [boardOptions, setBoardOptions] = useState([]);

    const [board, setBoard] = useState("CBSE");


    const [mediumId, setMediumId] = useState("");
    const [mediumName, setMediumName] = useState("");
    const [mediumOptions, setMediumOptions] = useState([]);

    const [classId, setClassId] = useState("");
    const [className, setClassName] = useState("");
    const [classOptions, setClassOptions] = useState([]);

    const [subjectId, setSubjectId] = useState("");
    const [subjectName, setSubjectName] = useState("");
    const [subjectOptions, setSubjectOptions] = useState([]);

    // Fetch dropdown options
    useEffect(() => {
        const fetchDropdowns = async () => {
            try {
                setLoading(true);
                const [boardRes, mediumRes, classRes, subjectRes] = await Promise.all([
                    window.Platform.database.getBoard(),
                    window.Platform.database.getAllOlympiadMediums(),
                    window.Platform.database.getAllOlympiadClasses(),
                    window.Platform.database.getAllOlympiadSubjects(),
                ]);

                setBoardOptions(Array.isArray(boardRes) ? boardRes : boardRes?.data || []);
                setMediumOptions(Array.isArray(mediumRes) ? mediumRes : mediumRes?.data || []);
                setClassOptions(Array.isArray(classRes) ? classRes : classRes?.data || []);
                setSubjectOptions(Array.isArray(subjectRes) ? subjectRes : subjectRes?.data || []);
            } catch (err) {
                window.NotificationUtils.showError(err?.message);
            } finally {
                setLoading(false);
            }
        };

        fetchDropdowns();
    }, []);

    // Prefill basic fields + ids/names directly available from propData
    useEffect(() => {
        if (propData) {
            setProductName(propData.product_name || "");
            setSkuCode(propData.sku_code || "");
            setUnitPrice(propData.price ?? "");

            // board: only name available, resolve id from options
            // setBoardName(propData.board || "");
              setBoard(propData.board || "CBSE");

            setMediumId(propData.medium_id || "");
            setMediumName(propData.medium_name || "");

            setClassId(propData.class_id || "");
            setClassName(propData.class_name || "");

            setSubjectId(propData.subject_id || "");
            setSubjectName(propData.subject_name || "");
        }
    }, [propData]);

    // Resolve boardId from boardName once board options are loaded
    useEffect(() => {
        if (propData?.board && boardOptions.length) {
            const match = boardOptions.find((b) => b.boardName === propData.board);
            setBoardId(match ? match.boardId : "");
        }
    }, [propData, boardOptions]);

    const handleBoardChange = (e) => {
        const selectedName = e.target.value;
        const selected = boardOptions.find((b) => b.boardName === selectedName);
        setBoardName(selectedName);
        setBoardId(selected ? selected.boardId : "");
    };

    const handleMediumChange = (e) => {
        const selectedName = e.target.value;
        const selected = mediumOptions.find((m) => m.medium_name === selectedName);
        setMediumName(selectedName);
        setMediumId(selected ? selected.id : "");
    };

    const handleClassChange = (e) => {
        const selectedName = e.target.value;
        const selected = classOptions.find((c) => c.class_name === selectedName);
        setClassName(selectedName);
        setClassId(selected ? selected.id : "");
    };

    const handleSubjectChange = (e) => {
        const selectedName = e.target.value;
        const selected = subjectOptions.find((s) => s.subject_name === selectedName);
        setSubjectName(selectedName);
        setSubjectId(selected ? selected.id : "");
    };

    const submitHandler = async () => {
        if (
            !productName.trim() ||
            !skuCode.trim() ||
            unitPrice === "" ||
            // !boardId ||
            !mediumId ||
            !classId ||
            !subjectId
        ) {
            window.NotificationUtils.showError("Please fill in all required fields");
            return;
        }

        setLoading(true);
        try {
            const payload = {
                product_name: productName,
                sku_code: skuCode,
                price: Number(unitPrice),
                // boardId,
                board : board,
                medium_id : mediumId,
                class_id : classId,
                subject_id : subjectId,
            };

            if (propData) {
                await window.Platform.database.updateSubjectSkuMapping({
                    ...payload,
                    id: propData.id,
                });
                window.NotificationUtils.showSuccess("Mapping updated successfully");
            } else {
                await window.Platform.database.createSubjectSkuMapping(payload);
                window.NotificationUtils.showSuccess("Mapping saved successfully");
            }

            fetchData();
            onClose();
        } catch (err) {
            window.NotificationUtils.showError(err?.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog disableEnforceFocus maxWidth="sm" fullWidth open>
            <StyledDialogContent>
                <ModalHeaderContainer>
                    <Typography variant="h5">
                        {propData ? "Update Mapping" : "Add Mapping"}
                    </Typography>
                    <IconButton onClick={onClose}>
                        <CloseOutlined />
                    </IconButton>
                </ModalHeaderContainer>

                {loading && <OpaqueLoading />}

                <Stack gap={2} padding={2}>
                    <FormControl fullWidth>
                        <TextField
                            label="Product Name"
                            value={productName}
                            type="text"
                            onChange={(e) => setProductName(e.target.value)}
                            fullWidth
                        />
                    </FormControl>

                    <FormControl fullWidth>
                        <TextField
                            label="SKU Code"
                            value={skuCode}
                            type="text"
                            onChange={(e) => setSkuCode(e.target.value)}
                            fullWidth
                        />
                    </FormControl>

                    <FormControl fullWidth>
                        <TextField
                            label="Unit Price"
                            value={unitPrice}
                            type="number"
                            onChange={(e) => setUnitPrice(e.target.value)}
                            onWheel={(e) => e.target.blur()}
                            fullWidth
                        />
                    </FormControl>
{/* 
                    <FormControl fullWidth>
                        <InputLabel id="board-label">Board</InputLabel>
                        <Select
                            labelId="board-label"
                            label="Board"
                            value={boardName}
                            onChange={handleBoardChange}
                        >
                            {boardOptions.map((b) => (
                                <MenuItem key={b.boardId} value={b.boardName}>
                                    {b.boardName}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl> */}
                    <FormControl fullWidth>
    <TextField
        label="Board"
        value={board}
        type="text"
        InputProps={{ readOnly: true }}
        fullWidth
    />
</FormControl>

                    <FormControl fullWidth>
                        <InputLabel id="medium-label">Medium</InputLabel>
                        <Select
                            labelId="medium-label"
                            label="Medium"
                            value={mediumName}
                            onChange={handleMediumChange}
                        >
                            {mediumOptions.map((m) => (
                                <MenuItem key={m.id} value={m.medium_name}>
                                    {m.medium_name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl fullWidth>
                        <InputLabel id="class-label">Class</InputLabel>
                        <Select
                            labelId="class-label"
                            label="Class"
                            value={className}
                            onChange={handleClassChange}
                        >
                            {classOptions.map((c) => (
                                <MenuItem key={c.id} value={c.class_name}>
                                    {c.class_name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl fullWidth>
                        <InputLabel id="subject-label">Subject</InputLabel>
                        <Select
                            labelId="subject-label"
                            label="Subject"
                            value={subjectName}
                            onChange={handleSubjectChange}
                        >
                            {subjectOptions.map((s) => (
                                <MenuItem key={s.id} value={s.subject_name}>
                                    {s.subject_name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                        <Button
                            color="primary"
                            variant="contained"
                            onClick={submitHandler}
                            disabled={!productName || !skuCode}
                        >
                            {propData ? "Update" : "Create"}
                        </Button>
                    </div>
                </Stack>
            </StyledDialogContent>
        </Dialog>
    );
};

export default OlympiadSkuSubjectMappingModal;